import _isNil from 'lodash/isNil';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {ContentCardScratchpadState} from '../../scratchpad/contentCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {contentSetsApi} from 'common/src/modules/contentSets/contentSets.api';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const inputSchema = z.object({
  contentSetId: z
    .string()
    .optional()
    .describe(`${describeIdFormat(IdPrefix.ContentSet)} Leave empty to remove the content set.`),
});

async function execute({
  input: {contentSetId},
  toolUtils: {agentScratchpad},
}: ToolExecuteParams<typeof inputSchema, ContentCardScratchpadState>): Promise<ToolResult> {
  const validatedScratchpad = validateAgentScratchpad<ContentCardScratchpadState>(
    agentScratchpad,
    NodeType.CONTENT_FEED,
  );

  if (_isNil(contentSetId) || contentSetId === '') {
    validatedScratchpad.card.contentSetId = undefined;
    validatedScratchpad.card.contentSet = undefined;

    return toolResult('Content set removed');
  }

  const parseResult = idHelper.parseContentSetId(contentSetId);

  if (parseResult.ok === false) {
    return toolError(formatParseError(parseResult.error));
  }

  const numericId = parseResult.numericId;
  const availableSets = await contentSetsApi.getContentSets(validatedScratchpad.card.contentFeedId);

  if (!availableSets.some((s) => s.id === numericId)) {
    return toolError(
      `Content set ${idHelper.toContentSetId(numericId)} is not available. Available content sets: ${availableSets.map((s) => `${idHelper.toContentSetId(s.id)}: ${s.name}`).join(', ')}`,
    );
  }

  validatedScratchpad.card.contentSetId = numericId;

  return toolResult(`Content set set to ${idHelper.toContentSetId(numericId)}`);
}

export default function setContentSetTool() {
  return tool({
    name: V2CardAgentToolName.SetContentSet,
    displayName: 'Setting content set',
    description: 'Set the content set for the card.',
    inputSchema,
    execute,
  });
}
